// src/pages/AdminsPage.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAdmins, deleteAdmin } from "../../redux/slices/adminSlice";
import AdminTable from "../../components/Admins/AdminTable";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useTranslation } from "../../contexts/LanguageContext";

function AdminsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { admins, loading, error } = useSelector((state) => state.admins);
  
  // State for filter and sort parameters
  const [filterParams, setFilterParams] = useState({
    byKeyword: "", // Changed from byAdminId and byKeyword to just byKeyword
    limit: 10,
    currentPage: 1,
  });
  const [sortBy, setSortBy] = useState("Name");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchAdmins(filterParams));
  }, [dispatch, filterParams]);

  const handleAddMember = () => {
    navigate("/create-admin");
  };

  const handleEdit = (admin) => {
    navigate("/create-admin", { state: { admin } });
  };

  const openDeleteModal = (adminId) => {
    setAdminToDelete(adminId);
    setIsDeleteModalOpen(true);
  };

  // Updated delete handler: Uses the confirmation modal
  const handleDelete = () => {
    if (!adminToDelete) return;
    
    setIsDeleting(true);
    dispatch(deleteAdmin(adminToDelete))
      .unwrap()
      .then(() => {
        setIsDeleteModalOpen(false);
        setAdminToDelete(null);
      })
      .catch((error) => {
        console.error("Failed to delete admin:", error);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleFilter = (filterObj) => {
    setFilterParams(filterObj);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  // Sort the admins array locally based on sortBy state
  const sortedAdmins = admins.slice().sort((a, b) => {
    if (sortBy === "Name") {
      return a.name.localeCompare(b.name);
    } else if (sortBy === "Email") {
      return a.email.localeCompare(b.email);
    }
    return 0;
  });

  return (
    <div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
        </div>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <AdminTable 
          admins={sortedAdmins} 
          onEdit={handleEdit} 
          onDelete={openDeleteModal}
          onAddNew={handleAddMember}
          total={admins.length}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t("deleteConfirm.admin.title")}
        message={t("deleteConfirm.admin.message")}
        confirmText={t("deleteConfirm.delete")}
        cancelText={t("deleteConfirm.cancel")}
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default AdminsPage;
